import type { ComponentPublicInstance } from 'vue';
import type { Router } from 'vue-router';
import { isNuxtError } from '#app';
import { COOKIE_NAME_AREA_SWITCH_BAND } from '~/types/cookieNames';
import { extractStatusCode } from '~/utils/extractStatusCodeFromError';
import log from '~/utils/log';
import { parseSentrySampleRate } from '~/utils/sentry';

// Replicates the behavior of the deprecated `sendDefaultPii: false` (see
// https://docs.sentry.io/platforms/javascript/configuration/options/), which gets removed in v11
const piiDenyList = ['forwarded', '-ip', 'remote-', 'via', '-user'];
// The value of the area switch band cookie holds a URL with a query. The query can hold personal data.
const cookieDenyList = [...piiDenyList, COOKIE_NAME_AREA_SWITCH_BAND];
// `@sentry/core` is not a direct dependency, so derive the option type from the SDK we do depend on
type DataCollection = NonNullable<NonNullable<Parameters<typeof import('@sentry/nuxt').init>[0]>['dataCollection']>;
const dataCollection = {
    userInfo: false,
    cookies: { deny: cookieDenyList },
    httpHeaders: {
        request: { deny: piiDenyList },
        response: { deny: piiDenyList }
    },
    httpBodies: [],
    urlQueryParams: { deny: piiDenyList },
    genAI: { inputs: false, outputs: false }
} satisfies DataCollection;

const tracePropagationTargets = [
    'localhost',
    /^https:\/\/hfp\.internal(?::\d+)?(?:\/|$)/,
    /^https:\/\/stage\.medice-health-family\.com(?::\d+)?(?:\/|$)/,
    /^https:\/\/medice-health-family\.com(?::\d+)?(?:\/|$)/,
    /^\//
];

interface SentryModule {
    init: typeof import('@sentry/nuxt').init;
    captureException: typeof import('@sentry/nuxt').captureException;
    close: typeof import('@sentry/nuxt').close;
}

export default defineNuxtPlugin((nuxtApp) => {
    const {
        public: { sentryDsn, sentryEnvironment, sentryRelease, sentryTracesSampleRate, sentryDebug }
    } = useRuntimeConfig();

    if (!sentryDsn) return;

    const sentryConsent = useUsercentricsConsentItem('sentry');
    let sentryModule: SentryModule | null = null;
    let isSentryInitialized = false;
    let isSentryInitializing = false;
    let areErrorHooksRegistered = false;

    const reportNuxtError = (options: { error: unknown; instance?: ComponentPublicInstance | null; info?: string }) => {
        const { error, instance, info } = options;

        setTimeout(() => {
            if (!isSentryInitialized || sentryConsent.value !== true) return;

            sentryModule?.captureException(error, {
                captureContext: { contexts: { nuxt: { info } } },
                mechanism: { handled: false, type: `auto.function.nuxt.${instance ? 'vue' : 'app'}-error` }
            });
        });
    };

    const registerErrorHooks = () => {
        if (areErrorHooksRegistered) return;

        areErrorHooksRegistered = true;

        nuxtApp.hook('app:error', (error) => {
            const statusCode = isNuxtError(error) ? extractStatusCode(error) : undefined;

            if (statusCode && statusCode >= 300 && statusCode < 500) return;

            reportNuxtError({ error });
        });

        nuxtApp.hook('vue:error', (error, instance, info) => {
            reportNuxtError({ error, instance, info });
        });
    };

    const initializeSentry = async () => {
        if (isSentryInitialized || isSentryInitializing || sentryConsent.value !== true) return;

        isSentryInitializing = true;

        try {
            // `@sentry/nuxt` re-exports the `@sentry/vue` client API, so it covers the tracing integration too
            const Sentry = await import('@sentry/nuxt');

            if (sentryConsent.value !== true) return;

            sentryModule = Sentry;
            Sentry.init({
                dsn: sentryDsn,
                environment: sentryEnvironment,
                release: sentryRelease,
                tracePropagationTargets,
                tracesSampleRate: parseSentrySampleRate(sentryTracesSampleRate),
                dataCollection,
                debug: sentryDebug === true,
                integrations: [
                    Sentry.browserTracingIntegration({ router: nuxtApp.$router as Router, routeLabel: 'path' })
                ]
            });

            isSentryInitialized = true;
            registerErrorHooks();
        } catch (error) {
            log.warn('Failed to initialize Sentry', error);
        } finally {
            isSentryInitializing = false;
        }
    };

    const closeSentry = () => {
        if (!isSentryInitialized) return;

        isSentryInitialized = false;
        void sentryModule?.close(2000);
    };

    const applyConsent = () => {
        if (sentryConsent.value === true) {
            void initializeSentry();
        } else {
            closeSentry();
        }
    };

    refreshUsercentricsConsentCache()
        .then(applyConsent)
        .catch((error) => {
            log.warn('Failed to refresh Usercentrics consent state', error);
        });

    onConsentUpdated(applyConsent);
});
